<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { fetchCompetenceDomainTemplatesForSchoolLevel } from '@/api/resources/competenceDomain.api';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { usePermissions } from '@/composables/usePermissions';
import SchoolSubjectSelectionDialog from '@/core/dialogs/create-class/SchoolSubjectSelectionDialog.vue';
import AddButton from '@/core/elements/AddButton.vue';
import CustomButton from '@/core/elements/CustomButton.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import SchoolSubjectGroupCheckboxList from '@/core/selects/SchoolSubjectGroupCheckboxList.vue';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { adjustSubjectNaming, getOrderedSchoolSubjects } from 'common/utils/school.subject.utils';
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const { canManageCompetences, canAddCompetences } = usePermissions();
const { push } = useRouter();
const { currentTenant } = useCurrentTenant();
const { allSchoolSubjectTemplates } = useSchoolSubject();

const emit = defineEmits<{
  continue: [value: Components.Schemas.SchoolSubjectDto[]];
  'update:isValid': [value: boolean];
  'update:availableSchoolSubjects': [value: Components.Schemas.SchoolSubjectDto[]];
}>();

const props = defineProps<{
  schoolLevel?: number;
  schoolSubjects: Components.Schemas.SchoolSubjectDto[];
  availableSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
  selectAllInitially?: boolean;
  singleSelect?: boolean;
  availableSchoolSubjectGroups: Components.Schemas.SchoolSubjectGroupDto[];
}>();
const { schoolLevel, schoolSubjects, availableSchoolSubjects, availableSchoolSubjectGroups } = toRefs(props);

const schoolSubjectSelectionDialogRef = useTemplateRef('schoolSubjectSelectionDialogRef');
const selectedSchoolSubjects = ref<Components.Schemas.SchoolSubjectDto[]>([]);
const availableCompetenceDomains = ref<Components.Schemas.CompetenceDomainDto[]>([]);

const sortedSelectedSchoolSubjects = computed<Components.Schemas.SchoolSubjectDto[]>(() =>
  getOrderedSchoolSubjects(selectedSchoolSubjects.value, availableSchoolSubjectGroups.value),
);

const updateCompetenceDomains = async (): Promise<void> => {
  if (schoolLevel.value !== undefined)
    availableCompetenceDomains.value = await fetchCompetenceDomainTemplatesForSchoolLevel(schoolLevel.value);
};

const onSubmit = async (): Promise<void> => {
  emit('continue', sortedSelectedSchoolSubjects.value);
};

const updateSelectedSchoolSubjects = (): void => {
  const existingSchoolSubjectIds = schoolSubjects.value.map((subject) => subject.originId);
  // preselect all available school subjects if the user hasn't selected any yet
  if (!existingSchoolSubjectIds.length) {
    if (props.singleSelect) {
      selectedSchoolSubjects.value = [];
      return;
    }
    selectedSchoolSubjects.value = props.selectAllInitially
      ? availableSchoolSubjects.value
      : availableSchoolSubjects.value.filter((subject) => subject.isPreselected);
    return;
  }

  selectedSchoolSubjects.value = availableSchoolSubjects.value.filter((subject) =>
    existingSchoolSubjectIds.includes(subject.originId),
  );
};

const addSchoolSubjectFromOtherLevels = (): void => {
  assertNotNullOrUndefined(schoolLevel.value);
  schoolSubjectSelectionDialogRef.value?.show({ schoolLevel: schoolLevel.value });
};

const onNewSchoolSubjectsSelected = (newSchoolSubjects: Components.Schemas.SchoolSubjectDto[]): void => {
  assertNotNullOrUndefined(schoolLevel.value);
  const currentSchoolLevel = schoolLevel.value;
  const adjustedSubjects = newSchoolSubjects.map((subject) => ({
    ...subject,
    ...adjustSubjectNaming(subject, currentSchoolLevel),
  }));

  selectedSchoolSubjects.value = [...selectedSchoolSubjects.value, ...adjustedSubjects];

  emit('update:availableSchoolSubjects', [...availableSchoolSubjects.value, ...adjustedSubjects]);
};

const goToSchoolCompetences = async (): Promise<void> => {
  assertNotNullOrUndefined(currentTenant.value?.id);
  await push({
    name: '//school/[schoolId]/settings/schoolSubjectTemplates/',
    params: { schoolId: currentTenant.value.id },
  });
};

const hasOtherLevelSubjects = computed<boolean>(() => {
  return allSchoolSubjectTemplates.value.some((sS) => sS.schoolLevel !== schoolLevel.value);
});

watch(schoolSubjects, updateSelectedSchoolSubjects, { immediate: true });
watch(schoolLevel, updateCompetenceDomains, { immediate: true });
watch(
  selectedSchoolSubjects,
  () => {
    const isValid = props.singleSelect
      ? selectedSchoolSubjects.value.length === 1
      : selectedSchoolSubjects.value.length > 0;
    emit('update:isValid', isValid);
  },
  { immediate: true },
);

defineExpose({
  onSubmit,
});
</script>

<template>
  <div>
    <form class="grid h-full grid-rows-[auto_1fr] px-lg md:px-content" @submit.prevent="onSubmit">
      <p v-if="!singleSelect" class="text-subtitle mb-md">
        {{
          canManageCompetences ? $t('classes.schoolSubjectsDescription') : $t('classes.schoolSubjectsDescriptionAlt')
        }}
      </p>
      <div class="flex flex-col gap-md">
        <div v-if="availableSchoolSubjects.length" class="bg-surface-secondary rounded-md pb-sm">
          <SchoolSubjectGroupCheckboxList
            v-model="selectedSchoolSubjects"
            :disabled="!canManageCompetences"
            :single-select="singleSelect"
            :available-school-subjects="availableSchoolSubjects"
            :available-school-subject-groups="availableSchoolSubjectGroups"
            :available-competence-domains="availableCompetenceDomains"
          />
        </div>
        <div v-else class="flex flex-col gap-md">
          <EmptyPlaceholder :data="availableSchoolSubjects" :message="$t('schoolSubjects.noSubjects')" />
          <CustomButton theme="link" class="self-center break-words hyphens-auto" @click="goToSchoolCompetences">
            {{ $t('classes.createCompetences') }}
          </CustomButton>
        </div>

        <AddButton
          v-if="!singleSelect && canAddCompetences && hasOtherLevelSubjects"
          :label="$t('classes.addSchoolSubjectFromOtherLevels')"
          theme="text"
          class="md:w-min md:whitespace-nowrap max-md:px-0 max-md:items-start"
          @click="addSchoolSubjectFromOtherLevels"
        />
      </div>
    </form>
    <SchoolSubjectSelectionDialog
      ref="schoolSubjectSelectionDialogRef"
      :title="$t('schoolSubjects.schoolTemplate')"
      @continue="onNewSchoolSubjectsSelected"
    />
  </div>
</template>
